import { useState, useRef } from 'react';
import { api } from '../api';
import { Upload, CheckCircle, AlertTriangle, XCircle, Download } from 'lucide-react';

const COMPANY_HEADERS = ['name','type','website','phone','address_line1','address_line2','city','county','postcode','sector','notes'];
const CONTACT_HEADERS = ['first_name','last_name','company_name','job_title','email','phone','mobile','notes'];

const COMPANY_SAMPLE = [
  COMPANY_HEADERS.join(','),
  'Acme Energy Ltd,client,https://acme.com,01234567890,1 High Street,,Manchester,Greater Manchester,M1 1AA,Energy,Key account',
  'Northern Grid,dno,,01234000000,Grid House,,Leeds,West Yorkshire,LS1 1BB,Infrastructure,',
].join('\n');

const CONTACT_SAMPLE = [
  CONTACT_HEADERS.join(','),
  'John,Smith,Acme Energy Ltd,Contracts Manager,john@acme.com,07700900000,,Key decision maker',
  'Sarah,Jones,Northern Grid,Engineer,sarah@ngrid.com,,,',
].join('\n');

function parseCSV(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  if (lines.length < 2) return { headers: [], rows: [] };
  const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g,'_'));
  const rows = lines.slice(1).map(line => {
    const vals = line.split(',').map(v => v.trim());
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] || ''; });
    return obj;
  });
  return { headers, rows };
}

function downloadSample(type) {
  const content = type === 'companies' ? COMPANY_SAMPLE : CONTACT_SAMPLE;
  const blob = new Blob([content], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = `ipe_${type}_template.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default function ImportPage() {
  const [tab, setTab]           = useState('companies');
  const [file, setFile]         = useState(null);
  const [preview, setPreview]   = useState(null);
  const [result, setResult]     = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const fileRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    setFile(f); setResult(null); setError('');
    const reader = new FileReader();
    reader.onload = (e) => {
      const { headers, rows } = parseCSV(e.target.result);
      setPreview({ headers, rows, raw: e.target.result });
    };
    reader.readAsText(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith('.csv')) handleFile(f);
    else setError('Please drop a .csv file');
  };

  const handleImport = async () => {
    if (!preview?.rows?.length) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await api.post(`/api/import/${tab}`, { rows: preview.rows });
      setResult(res.data);
      setFile(null); setPreview(null);
      if (fileRef.current) fileRef.current.value = '';
    } catch(e) {
      setError(e.response?.data?.error || 'Import failed');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); setError(''); if (fileRef.current) fileRef.current.value = ''; };

  const expectedHeaders = tab === 'companies' ? COMPANY_HEADERS : CONTACT_HEADERS;
  const requiredHeaders = tab === 'companies' ? ['name','type'] : ['first_name','last_name','company_name'];

  const headerMismatch = preview && !requiredHeaders.every(h => preview.headers.includes(h));

  return (
    <div style={{ padding:'28px 32px', maxWidth:900, margin:'0 auto' }}>
      <h2 style={{ color:'var(--text)', fontSize:20, fontWeight:700, margin:'0 0 4px' }}>CSV Import</h2>
      <p style={{ color:'var(--text-dim)', fontSize:13, margin:'0 0 24px' }}>Import Companies or Contacts from a CSV file. Download the template to ensure correct formatting.</p>

      {/* Tab selector */}
      <div style={{ display:'flex', gap:8, marginBottom:24 }}>
        {['companies','contacts'].map(t => (
          <button key={t} onClick={() => { setTab(t); reset(); }}
            style={{ padding:'8px 20px', borderRadius:8, border:'none', fontSize:13, fontWeight:600, cursor:'pointer', textTransform:'capitalize',
              background: tab === t ? '#3b82f6' : 'var(--surface2)',
              color: tab === t ? '#fff' : 'var(--text-dim)' }}>
            {t}
          </button>
        ))}
      </div>

      {/* Template download */}
      <div style={{ marginBottom:20, padding:'12px 16px', background:'rgba(96,165,250,0.06)', border:'1px solid rgba(96,165,250,0.18)', borderRadius:8, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <p style={{ color:'#60a5fa', fontSize:12, fontWeight:600, margin:'0 0 2px', textTransform:'uppercase', letterSpacing:0.5 }}>CSV Template</p>
          <p style={{ color:'var(--text-dim)', fontSize:12, margin:0 }}>Required columns: <strong style={{color:'var(--text)'}}>{requiredHeaders.join(', ')}</strong>. Optional: {expectedHeaders.filter(h => !requiredHeaders.includes(h)).join(', ')}</p>
        </div>
        <button onClick={() => downloadSample(tab)}
          style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', background:'rgba(96,165,250,0.12)', border:'1px solid rgba(96,165,250,0.3)', borderRadius:7, color:'#60a5fa', fontSize:12, fontWeight:600, cursor:'pointer', whiteSpace:'nowrap', marginLeft:16 }}>
          <Download size={13}/> Download Template
        </button>
      </div>

      {/* Drop zone */}
      {!preview && !result && (
        <div onDrop={handleDrop} onDragOver={e => e.preventDefault()}
          style={{ border:'2px dashed var(--border)', borderRadius:10, padding:'40px 20px', textAlign:'center', cursor:'pointer', background:'var(--surface2)', transition:'border-color 0.2s' }}
          onClick={() => fileRef.current?.click()}>
          <Upload size={28} color="#60a5fa" style={{ marginBottom:10 }}/>
          <p style={{ color:'var(--text)', fontSize:14, margin:'0 0 4px', fontWeight:500 }}>Drop your CSV file here or click to browse</p>
          <p style={{ color:'var(--text-dim)', fontSize:12, margin:0 }}>Accepts .csv files only</p>
          <input ref={fileRef} type="file" accept=".csv" style={{ display:'none' }} onChange={e => handleFile(e.target.files[0])} />
        </div>
      )}

      {error && <p style={{ color:'#f87171', fontSize:13, margin:'12px 0 0', display:'flex', alignItems:'center', gap:6 }}><XCircle size={14}/>{error}</p>}

      {/* Preview */}
      {preview && !result && (
        <div style={{ marginTop:16 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:10 }}>
            <p style={{ color:'var(--text)', fontSize:13, fontWeight:600, margin:0 }}>{file?.name} — {preview.rows.length} row{preview.rows.length !== 1 ? 's' : ''} detected</p>
            <button onClick={reset} style={{ background:'transparent', border:'none', color:'var(--text-dim)', fontSize:12, cursor:'pointer' }}>✕ Clear</button>
          </div>

          {headerMismatch && (
            <div style={{ marginBottom:12, padding:'10px 14px', background:'rgba(239,68,68,0.08)', border:'1px solid rgba(239,68,68,0.25)', borderRadius:8 }}>
              <p style={{ color:'#f87171', fontSize:12, margin:0, display:'flex', alignItems:'center', gap:6 }}>
                <AlertTriangle size={13}/> Missing required columns: {requiredHeaders.filter(h => !preview.headers.includes(h)).join(', ')}. Please fix your CSV and re-upload.
              </p>
            </div>
          )}

          {/* Preview table — first 5 rows */}
          <div style={{ overflowX:'auto', borderRadius:8, border:'1px solid var(--border)', marginBottom:14 }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
              <thead>
                <tr style={{ background:'var(--surface2)' }}>
                  {preview.headers.map(h => (
                    <th key={h} style={{ padding:'8px 12px', textAlign:'left', color: requiredHeaders.includes(h) ? '#60a5fa' : 'var(--text-dim)', fontWeight:600, whiteSpace:'nowrap' }}>
                      {h}{requiredHeaders.includes(h) ? ' *' : ''}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.rows.slice(0,5).map((row, i) => (
                  <tr key={i} style={{ borderTop:'1px solid var(--border)' }}>
                    {preview.headers.map(h => (
                      <td key={h} style={{ padding:'7px 12px', color:'var(--text-dim)', maxWidth:160, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{row[h] || '—'}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {preview.rows.length > 5 && <p style={{ color:'var(--text-dim)', fontSize:11, margin:'0 0 12px' }}>Showing first 5 of {preview.rows.length} rows</p>}

          <div style={{ display:'flex', gap:8 }}>
            <button onClick={reset} style={{ padding:'9px 18px', background:'transparent', border:'1px solid var(--border)', borderRadius:8, color:'var(--text-dim)', fontSize:13, cursor:'pointer' }}>Cancel</button>
            <button onClick={handleImport} disabled={loading || headerMismatch}
              style={{ padding:'9px 22px', background: headerMismatch ? 'rgba(59,130,246,0.3)' : '#3b82f6', border:'none', borderRadius:8, color:'#fff', fontSize:13, fontWeight:600, cursor: headerMismatch ? 'not-allowed' : 'pointer' }}>
              {loading ? 'Importing…' : `Import ${preview.rows.length} row${preview.rows.length !== 1 ? 's' : ''}`}
            </button>
          </div>
        </div>
      )}

      {/* Result */}
      {result && (
        <div style={{ marginTop:16 }}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:16 }}>
            {[
              { label:'Imported', count: result.imported, color:'#10b981', bg:'rgba(16,185,129,0.08)', border:'rgba(16,185,129,0.2)', icon:<CheckCircle size={16}/> },
              { label:'Skipped (duplicates)', count: result.skipped, color:'#f59e0b', bg:'rgba(245,158,11,0.08)', border:'rgba(245,158,11,0.2)', icon:<AlertTriangle size={16}/> },
              { label:'Errors', count: result.errors, color:'#ef4444', bg:'rgba(239,68,68,0.08)', border:'rgba(239,68,68,0.2)', icon:<XCircle size={16}/> },
            ].map(s => (
              <div key={s.label} style={{ padding:'14px 16px', background:s.bg, border:`1px solid ${s.border}`, borderRadius:8, display:'flex', alignItems:'center', gap:10 }}>
                <span style={{ color:s.color }}>{s.icon}</span>
                <div>
                  <p style={{ color:s.color, fontSize:20, fontWeight:700, margin:0 }}>{s.count}</p>
                  <p style={{ color:'var(--text-dim)', fontSize:11, margin:0 }}>{s.label}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Error / skip detail */}
          {(result.detail.errors.length > 0 || result.detail.skipped.length > 0) && (
            <div style={{ border:'1px solid var(--border)', borderRadius:8, overflow:'hidden', marginBottom:14 }}>
              <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                <thead><tr style={{ background:'var(--surface2)' }}>
                  <th style={{ padding:'8px 12px', textAlign:'left', color:'var(--text-dim)' }}>Row</th>
                  <th style={{ padding:'8px 12px', textAlign:'left', color:'var(--text-dim)' }}>Type</th>
                  <th style={{ padding:'8px 12px', textAlign:'left', color:'var(--text-dim)' }}>Reason</th>
                </tr></thead>
                <tbody>
                  {result.detail.errors.map((e,i) => (
                    <tr key={`e${i}`} style={{ borderTop:'1px solid var(--border)' }}>
                      <td style={{ padding:'7px 12px', color:'#f87171' }}>{e.row}</td>
                      <td style={{ padding:'7px 12px', color:'#f87171' }}>Error</td>
                      <td style={{ padding:'7px 12px', color:'var(--text-dim)' }}>{e.reason}</td>
                    </tr>
                  ))}
                  {result.detail.skipped.map((s,i) => (
                    <tr key={`s${i}`} style={{ borderTop:'1px solid var(--border)' }}>
                      <td style={{ padding:'7px 12px', color:'#f59e0b' }}>{s.row}</td>
                      <td style={{ padding:'7px 12px', color:'#f59e0b' }}>Skipped</td>
                      <td style={{ padding:'7px 12px', color:'var(--text-dim)' }}>{s.name} — {s.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <button onClick={reset} style={{ padding:'9px 22px', background:'#3b82f6', border:'none', borderRadius:8, color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer' }}>Import Another File</button>
        </div>
      )}
    </div>
  );
}
